import { useState, useEffect, useCallback } from 'react';
import {
  MapPin, Clock, Users, TrendingUp, RefreshCw, Calendar,
  ChevronRight, Activity, Zap, ArrowRight, X, Route, Brain, Play, Eye, EyeOff
} from 'lucide-react';
import { API } from '../../utils/apiConfig';

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const DAY_FULL = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

export const AdminCommuterTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [runningDetection, setRunningDetection] = useState(false);

  const headers = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [hotspotsRes, patternsRes] = await Promise.all([
        fetch(`${API}/orchestrator/admin/commuter/hotspots`, { headers: headers() }),
        fetch(`${API}/orchestrator/admin/commuter/patterns`, { headers: headers() }),
      ]);
      setData(await hotspotsRes.json());
      setPatterns(await patternsRes.json());
    } catch (e) { console.error('Load failed:', e); }
    setLoading(false);
  }, []);

  const runDetection = async () => {
    setRunningDetection(true);
    try {
      const res = await fetch(`${API}/commuter/patterns/run-detection`, {
        method: 'POST', headers: headers(),
      });
      const result = await res.json();
      alert(`Pattern Detection: ${result.new_patterns || 0} neue Muster erkannt`);
      loadData();
    } catch (e) { console.error('Detection failed:', e); }
    setRunningDetection(false);
  };

  const loadUserDetail = async (userId) => {
    try {
      const res = await fetch(`${API}/orchestrator/admin/commuter/user/${userId}`, { headers: headers() });
      const detail = await res.json();
      setUserDetail(detail);
      setSelectedUser(userId);
    } catch (e) { console.error('User detail failed:', e); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Lade Pendler-Daten...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Keine Daten verfuegbar</div>;

  const { route_clusters = [], day_clusters = [], time_slots = [], stats = {} } = data;

  return (
    <div data-testid="admin-commuter-tab" style={{ padding: '0 0 32px' }}>
      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard icon={<Users size={18} />} label="Aktive Pendler" value={stats.total_commuters || 0} color="#00d4ff" />
        <StatCard icon={<Route size={18} />} label="Aktive Suchen" value={stats.total_active_searches || 0} color="#22c55e" />
        <StatCard icon={<Zap size={18} />} label="Offene Matches" value={stats.pending_matches || 0}
          color={stats.pending_matches > 0 ? '#f59e0b' : '#94a3b8'} />
        <StatCard icon={<Brain size={18} />} label="Erkannte Muster"
          value={patterns?.stats?.pending || 0}
          color={patterns?.stats?.pending > 0 ? '#a78bfa' : '#94a3b8'} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button data-testid="run-detection" onClick={runDetection} disabled={runningDetection}
            style={{ ...refreshBtnStyle, background: 'rgba(167,139,250,0.1)', borderColor: 'rgba(167,139,250,0.3)', color: '#a78bfa' }}>
            <Play size={14} /> {runningDetection ? 'Analyse...' : 'Pattern-Scan'}
          </button>
          <button data-testid="commuter-refresh" onClick={loadData} style={refreshBtnStyle}>
            <RefreshCw size={14} /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Route Hotspots */}
      <SectionHeader title="Routen-Hotspots" subtitle="Meistgesuchte Pendelstrecken" />
      {route_clusters.length === 0 ? (
        <EmptyState text="Noch keine Pendler-Routen" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
          {route_clusters.map((c, i) => (
            <div key={i} data-testid={`route-cluster-${i}`} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
              borderRadius: 12, background: 'rgba(255,255,255,0.03)',
              border: c.active_count > 2 ? '1px solid rgba(0,212,255,0.3)' : '1px solid rgba(255,255,255,0.06)',
              transition: 'all 0.2s',
            }}>
              {/* Rank badge */}
              <div style={{
                width: 32, height: 32, borderRadius: 8, display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700,
                background: i < 3 ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)',
                color: i < 3 ? '#00d4ff' : '#64748b',
              }}>
                {i + 1}
              </div>

              {/* Route */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <MapPin size={13} style={{ color: '#22c55e' }} />
                  <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{c.from_city}</span>
                  <ArrowRight size={14} style={{ color: '#475569' }} />
                  <MapPin size={13} style={{ color: '#ef4444' }} />
                  <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{c.to_city}</span>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ color: '#64748b', fontSize: 11 }}>
                    <Clock size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                    {c.earliest_time || '?'} — {c.latest_time || '?'}
                  </span>
                  <span style={{ color: '#64748b', fontSize: 11 }}>
                    <Calendar size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                    {(c.popular_days || []).sort().map(d => DAYS[d]).join(', ')}
                  </span>
                  {c.avg_corridor && (
                    <span style={{ color: '#64748b', fontSize: 11 }}>Korridor: {c.avg_corridor}km</span>
                  )}
                </div>
              </div>

              {/* Count badge */}
              <div style={{
                padding: '6px 14px', borderRadius: 20, fontWeight: 700, fontSize: 16,
                background: c.search_count >= 5 ? 'rgba(239,68,68,0.15)' : c.search_count >= 2 ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.05)',
                color: c.search_count >= 5 ? '#f87171' : c.search_count >= 2 ? '#fbbf24' : '#94a3b8',
                border: c.search_count >= 5 ? '1px solid rgba(239,68,68,0.3)' : 'none',
              }}>
                {c.search_count}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Time-Slot Heatmap */}
      <SectionHeader title="Zeitfenster-Verteilung" subtitle="Wann wollen die Pendler los?" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {time_slots.map((t, i) => {
          const max = Math.max(...time_slots.map(s => s.count), 1);
          const intensity = t.count / max;
          return (
            <div key={i} data-testid={`time-slot-${i}`} style={{
              padding: '12px 18px', borderRadius: 10, textAlign: 'center',
              background: `rgba(0, 212, 255, ${0.05 + intensity * 0.2})`,
              border: `1px solid rgba(0, 212, 255, ${0.1 + intensity * 0.3})`,
              flex: '1 1 120px',
            }}>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{t.time_slot}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>{t.count}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>Pendler</div>
            </div>
          );
        })}
      </div>

      {/* Day Clusters */}
      <SectionHeader title="Tages-Cluster" subtitle="Route + Tag + Pendlerzahl" />
      {day_clusters.length === 0 ? (
        <EmptyState text="Noch keine Tages-Cluster" />
      ) : (
        <div style={{
          borderRadius: 12, overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.06)', marginBottom: 28,
        }}>
          <table data-testid="day-clusters-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th style={thStyle}>Tag</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Hin</th>
                <th style={thStyle}>Rueck</th>
                <th style={thStyle}>Pendler</th>
              </tr>
            </thead>
            <tbody>
              {day_clusters.map((c, i) => (
                <tr key={i} style={{
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: c.commuter_count >= 3 ? 'rgba(0,212,255,0.06)' : 'transparent',
                }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#f1f5f9' }}>{DAY_FULL[c.day_of_week]}</td>
                  <td style={tdStyle}>
                    <span style={{ color: '#22c55e' }}>{c.from_city}</span>
                    <ArrowRight size={12} style={{ margin: '0 4px', color: '#475569', verticalAlign: 'middle' }} />
                    <span style={{ color: '#ef4444' }}>{c.to_city}</span>
                  </td>
                  <td style={{ ...tdStyle, color: '#e2e8f0' }}>{c.earliest_forward} — {c.latest_forward}</td>
                  <td style={{ ...tdStyle, color: '#e2e8f0' }}>{c.earliest_return || '-'}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontWeight: 600, fontSize: 12,
                      background: c.commuter_count >= 5 ? 'rgba(239,68,68,0.15)' : c.commuter_count >= 2 ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.06)',
                      color: c.commuter_count >= 5 ? '#f87171' : c.commuter_count >= 2 ? '#fbbf24' : '#94a3b8',
                    }}>
                      {c.commuter_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detected Patterns — Erkannte, aber nicht aktivierte Pendler */}
      {patterns && (patterns.patterns?.length > 0 || patterns.potential_clusters?.length > 0) && (
        <>
          <SectionHeader title="Erkannte Pendler-Muster" subtitle="KI-erkannte Patterns aus Ride-Historie (noch nicht aktiviert)" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 28 }}>
            {(patterns.patterns || []).map((p, i) => (
              <div key={p.id || i} data-testid={`pattern-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                borderRadius: 10,
                background: p.status === 'detected' ? 'rgba(167,139,250,0.06)' : p.status === 'confirmed' ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
                border: p.status === 'detected' ? '1px solid rgba(167,139,250,0.2)' : '1px solid rgba(255,255,255,0.06)',
              }}>
                <Brain size={16} style={{ color: p.status === 'detected' ? '#a78bfa' : p.status === 'confirmed' ? '#4ade80' : '#475569', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 13 }}>{p.user_name || 'User'}</span>
                    <span style={{ color: '#475569', fontSize: 11 }}>{p.email}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ color: '#e2e8f0', fontSize: 12 }}>{p.from_city} → {p.to_city}</span>
                    <span style={{ color: '#64748b', fontSize: 11 }}>{DAY_FULL[p.day_of_week]} {p.avg_time}</span>
                    <span style={{ color: '#64748b', fontSize: 11 }}>{p.trip_count}x gefahren</span>
                  </div>
                </div>
                {/* Confidence */}
                <div style={{
                  padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  background: p.confidence >= 0.8 ? 'rgba(34,197,94,0.15)' : p.confidence >= 0.6 ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.05)',
                  color: p.confidence >= 0.8 ? '#4ade80' : p.confidence >= 0.6 ? '#fbbf24' : '#94a3b8',
                }}>
                  {Math.round(p.confidence * 100)}%
                </div>
                {/* Status */}
                <span style={{
                  padding: '3px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                  background: p.status === 'detected' ? 'rgba(167,139,250,0.15)' : p.status === 'confirmed' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
                  color: p.status === 'detected' ? '#a78bfa' : p.status === 'confirmed' ? '#4ade80' : '#f87171',
                }}>
                  {p.status === 'detected' ? 'ERKANNT' : p.status === 'confirmed' ? 'AKTIVIERT' : 'VERWORFEN'}
                </span>
              </div>
            ))}
          </div>

          {/* Potential new clusters */}
          {patterns.potential_clusters?.length > 0 && (
            <>
              <SectionHeader title="Potenzielle Cluster" subtitle="Mehrere User mit gleichem Pattern — noch nicht aktiv" />
              <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
                {patterns.potential_clusters.map((c, i) => (
                  <div key={i} style={{
                    padding: '14px 18px', borderRadius: 12, flex: '1 1 200px',
                    background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 4 }}>
                      {c.from_city} → {c.to_city}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#a78bfa' }}>{c.user_count} User</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      Confidence: {Math.round(parseFloat(c.avg_confidence) * 100)}% | Tage: {(c.days || []).map(d => DAYS[d]).join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <UserCommuteModal detail={userDetail} onClose={() => { setSelectedUser(null); setUserDetail(null); }} />
      )}
    </div>
  );
};

function UserCommuteModal({ detail, onClose }) {
  const { user, schedule, searches, matches } = detail;
  if (!user) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div style={{
        width: '100%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto',
        background: '#1e293b', borderRadius: 16, padding: 28,
        border: '1px solid rgba(255,255,255,0.1)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: 18 }}>{user.name}</h3>
            <span style={{ color: '#64748b', fontSize: 13 }}>{user.email} | {user.preferred_lang?.toUpperCase()}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Weekly Matrix */}
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10, fontWeight: 600 }}>PENDLER-MATRIX</h4>
          {schedule.length === 0 ? (
            <p style={{ color: '#475569', fontSize: 13 }}>Kein Pendler-Schedule</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {schedule.map(d => (
                <div key={d.day_of_week} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                  borderRadius: 8, background: d.active ? 'rgba(0,212,255,0.06)' : 'rgba(255,255,255,0.02)',
                  opacity: d.active ? 1 : 0.4,
                }}>
                  <span style={{ width: 24, fontWeight: 700, fontSize: 12, color: d.active ? '#00d4ff' : '#475569' }}>
                    {DAYS[d.day_of_week]}
                  </span>
                  {d.active ? (
                    <>
                      <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 500 }}>{d.time_forward}</span>
                      <ArrowRight size={10} style={{ color: '#475569' }} />
                      <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 500 }}>{d.time_return}</span>
                      <span style={{ color: '#475569', fontSize: 11, marginLeft: 'auto' }}>
                        {d.from_city} → {d.to_city} ({d.commute_corridor_km}km)
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#475569', fontSize: 11 }}>Inaktiv</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Matches */}
        {matches.length > 0 && (
          <div>
            <h4 style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10, fontWeight: 600 }}>MATCH-HISTORIE</h4>
            {matches.map(m => (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 8, background: 'rgba(255,255,255,0.02)', marginBottom: 4,
              }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: m.match_score >= 0.7 ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                  color: m.match_score >= 0.7 ? '#4ade80' : '#fbbf24',
                }}>
                  {Math.round(m.match_score * 100)}%
                </span>
                <span style={{ color: '#e2e8f0', fontSize: 12 }}>{m.from_city} → {m.to_city}</span>
                <span style={{ color: '#64748b', fontSize: 11, marginLeft: 'auto' }}>{m.driver_name}</span>
                <span style={{
                  padding: '2px 6px', borderRadius: 6, fontSize: 10,
                  background: m.status === 'confirmed' ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)',
                  color: m.status === 'confirmed' ? '#4ade80' : '#94a3b8',
                }}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      flex: '1 1 160px', padding: '18px 22px',
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ color }}>{icon}</span>
        <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: 16, fontWeight: 700 }}>{title}</h3>
      {subtitle && <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 12 }}>{subtitle}</p>}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{
      textAlign: 'center', padding: 40, color: '#475569', marginBottom: 24,
      background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)',
    }}>
      <Activity size={24} style={{ marginBottom: 8, opacity: 0.3 }} />
      <p style={{ fontSize: 13 }}>{text}</p>
    </div>
  );
}

const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};
const tdStyle = { padding: '10px 14px', color: '#e2e8f0' };
const refreshBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: 13,
};
